import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSDomBasics: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">DOM Basics</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    HTML is static text. The DOM is the live object model the browser builds from it — the tree JavaScript reads and changes to make pages interactive.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="11 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Browsers receive HTML:</Para>
   <CodeBlock snippets={[{ language: 'html', code: `<h1>Welcome</h1>\n<button>Login</button>` }]} defaultLanguage="html" id="dom-problem" />
   <Para>How can JavaScript read or modify these elements? If HTML were only text, there&apos;d be no dynamic updates, no interactivity, no real user experience. We need a structured representation of the page — that&apos;s the DOM.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">The <Mark>DOM (Document Object Model)</Mark> is a tree-like representation of an HTML document that lets JavaScript read, modify, create, and delete elements.</p>
   </div>
   <CodeBlock snippets={[{ language: 'text', code: `HTML → Browser → DOM Tree → JavaScript manipulation` }]} defaultLanguage="text" id="dom-pipeline" />
  </section>

  {/* 3. Why DOM Exists */}
  <section className="mb-12">
   <SectionHeading n={3}>Why the DOM Exists</SectionHeading>
   <TwoUp
    left={{ title: 'Without DOM', items: ['Static websites', 'No runtime changes'] }}
    right={{ title: 'With DOM', items: ['Dynamic websites', 'Updatable, interactive UI'] }}
   />
   <Para>Notifications, comments, loading products, showing errors, updating the cart — all rely on DOM manipulation.</Para>
  </section>

  {/* 4. HTML To DOM Conversion */}
  <section className="mb-12">
   <SectionHeading n={4}>HTML to DOM Conversion</SectionHeading>
   <CodeBlock snippets={[{ language: 'html', code: `<body>\n  <h1>Hello</h1>\n  <button>Login</button>\n</body>` }]} defaultLanguage="html" id="dom-html" />
   <CodeBlock snippets={[{ language: 'text', code: `Document\n└── body\n    ├── h1\n    │   └── "Hello"   (text node)\n    └── button\n        └── "Login"   (text node)` }]} defaultLanguage="text" id="dom-tree" />
   <Para>The browser builds this structure automatically.</Para>
  </section>

  {/* 5. Document Object */}
  <section className="mb-12">
   <SectionHeading n={5}>The document Object</SectionHeading>
   <Para>The <Code>document</Code> object is your entry point to the DOM:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(document);\n\ndocument.getElementById();\ndocument.querySelector();\ndocument.createElement();` }]} defaultLanguage="javascript" id="dom-document" />
   <Para>Most DOM operations start from <Code>document</Code>.</Para>
  </section>

  {/* 6. Nodes In DOM */}
  <section className="mb-12">
   <SectionHeading n={6}>Nodes in the DOM</SectionHeading>
   <Para>Everything in the DOM is a <Mark>node</Mark>:</Para>
   <div className="flex flex-wrap gap-2 mb-4">{['Document node', 'Element node', 'Text node', 'Comment node'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <CodeBlock snippets={[{ language: 'html', code: `<h1>Hello</h1>\n<!-- Element node: h1  |  Text node: "Hello" -->` }]} defaultLanguage="html" id="dom-nodes" />
  </section>

  {/* 7. Element Nodes */}
  <section className="mb-12">
   <SectionHeading n={7}>Element Nodes</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// <button>Submit</button>\nconst button = document.querySelector("button");\n// now JavaScript can interact with the element` }]} defaultLanguage="javascript" id="dom-element" />
  </section>

  {/* 8. DOM Is A Tree */}
  <section className="mb-12">
   <SectionHeading n={8}>The DOM Is a Tree</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `Document\n├── html\n├── head\n└── body\n    ├── div\n    ├── p\n    └── button` }]} defaultLanguage="text" id="dom-treestruct" />
   <Para>Understanding this tree is critical for traversal (covered in the next lesson).</Para>
  </section>

  {/* 9. Reading Content */}
  <section className="mb-12">
   <SectionHeading n={9}>Reading Content</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// <h1 id="title">Welcome</h1>\nconst title = document.getElementById("title");\nconsole.log(title.textContent);` }]} defaultLanguage="javascript" id="dom-read" />
   <Output>{`Welcome`}</Output>
  </section>

  {/* 10. Updating Content */}
  <section className="mb-12">
   <SectionHeading n={10}>Updating Content</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `title.textContent = "Hello Sachin";` }]} defaultLanguage="javascript" id="dom-update" />
   <Para>The DOM updates immediately and the browser re-renders the UI.</Para>
  </section>

  {/* 11. Common Production Uses */}
  <section className="mb-12">
   <SectionHeading n={11}>Common Production Uses</SectionHeading>
   <div className="flex flex-wrap gap-2">{['Cart count', 'Unread notifications', 'Live scores', 'Chat messages', 'Stock prices'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>All of these are DOM updates driven by JavaScript.</Para>
  </section>

  {/* 12. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={12}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What is the DOM?', 'Why is the DOM tree-structured?', 'Difference between HTML and DOM?', 'What is a node?', 'What is the document object?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="What is the difference between HTML and the DOM?">
    HTML is the static source text the server sends. The DOM is the live, in-memory object tree the browser builds from that HTML — and what JavaScript actually reads and mutates. Changing the DOM updates the page; it doesn&apos;t rewrite the original HTML.
   </QA>
   <QA q="What is a node?">
    The basic unit of the DOM tree. Everything is a node — the document, elements (<Code>h1</Code>, <Code>button</Code>), the text inside them, and even comments are all different node types.
   </QA>
  </section>

  {/* 13. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={13}>Mental Model</SectionHeading>
   <Para>HTML is the <Mark>blueprint</Mark>; the DOM is the <Mark>actual building model</Mark> you can walk through and rearrange. JavaScript modifies the model, not the raw blueprint.</Para>
  </section>

  {/* 14. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={14}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     The DOM is a tree-like object representation of an HTML document that allows JavaScript to dynamically interact with webpage content.
    </p>
   </div>
  </section>

  {/* 15. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={15}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    HTML is static. The DOM is a live object model. JavaScript interacts with the DOM to create dynamic user experiences.
   </Callout>
  </section>
 </div>
);

export default JSDomBasics;
